/* Core & libraries */
import React, { useState, useEffect, useRef } from "react";
import {useTheme} from "react-jss";

/* Helpers, constants, custom methods, internal libraries */
import { useLoyaltyService } from "services/loyaltyService";
import { useRedemptionHistoryService } from "services/redemptionHistoryService";
import { useUserContext, useUserUpdateContext } from "../../contexts/userContext";
import { useHasPermission } from "../../hooks/useHasPermission";
import useStorageState from "hooks/useStorageState";

/* Components */
import LoyaltyPageHeader from "../../components/loyaltyPageHeader";
import LoyaltyBadges from "components/loyaltyBadges";
import RedemptionHistory from "components/redemptionHistory";
import {REDEMPTION_HISTORY_GRID_TYPE} from "components/redemptionHistory/RedemptionHistoryFilters";

/* Styles */
import styles from "./styles";

const Loyalty = () => {
  const theme = useTheme();
  const classes = styles({ theme });

  const [gridType, setGridType, resetGridType] = useStorageState('grid-type', REDEMPTION_HISTORY_GRID_TYPE.COMPLETED);

  const [gridOptions, setGridOptions] = useState({
    page: 1, type: gridType
  });

  /**
   * TODO: If we are going to need to reset the state of the grid on normal page change (changing page from the menu list)
   * the following code should be uncommented in order to reset the stored value
   * useEffect(() => {
   *     return () => {
   *       resetGridType();
   *     }
   *   }, []);
   */

  useEffect(() => {
    setGridType(gridOptions.type);
  }, [gridOptions.type]);

  const {
    redemptionHistoryResponse,
    redemptionHistoryCountResponse,
    getRedemptionHistoryList,
    getRedemptionHistoryCount,
    handleDownloadReport
  } = useRedemptionHistoryService({gridOptions});

  const {
    loyaltyResponse,
    getLoyaltyList,
    addRedemption,
    removeFailedRedemptions,
    loyaltyAcceptedResponse,
    getLoyaltyTermsAccepted
  } = useLoyaltyService({getRedemptionHistoryCount});

  const userContext = useUserContext();
  const setUserDetails = useUserUpdateContext();
  const { checkPermission } = useHasPermission();

  const [showCompanyLoyalty, setShowCompanyLoyalty] = useState(userContext?.userInfo?.showCompanyLoyalty);
  const [lepCanAcceptTerms, setLepCanAcceptTerms] = useState(userContext?.userInfo?.lepCanAcceptTerms);
  const [lepTermsAccepted, setLepTermsAccepted] = useState(null);
  const hasFetched = useRef(false);

  const companyEntityId = userContext?.userInfo?.companyInfo?.companyEntityId;

  useEffect(() => {
    if (companyEntityId && !hasFetched.current) {
      hasFetched.current = true;
      getLoyaltyTermsAccepted(companyEntityId);
    }
  }, [companyEntityId, getLoyaltyTermsAccepted]);

  useEffect(() => {
    if (loyaltyAcceptedResponse !== undefined) {
      setLepTermsAccepted(loyaltyAcceptedResponse);
    }
  }, [loyaltyAcceptedResponse]);

  // Callback to update state
  const handleTermsAccepted = async () => {
    const updatedUserInfo = {
      ...userContext,
      userInfo: {
        ...userContext.userInfo,
        lepTermsAccepted: true,
      }
    };

    setUserDetails(updatedUserInfo);

    setShowCompanyLoyalty(updatedUserInfo.userInfo.showCompanyLoyalty);
    setLepCanAcceptTerms(updatedUserInfo.userInfo.lepCanAcceptTerms);
    setLepTermsAccepted(updatedUserInfo.userInfo.lepTermsAccepted);
  };

  useEffect(() => {
    setShowCompanyLoyalty(userContext?.userInfo?.showCompanyLoyalty);
    setLepCanAcceptTerms(userContext?.userInfo?.lepCanAcceptTerms);
    setLepTermsAccepted(userContext?.userInfo?.lepTermsAccepted);
  }, [userContext?.userInfo?.lepTermsAccepted]);

  return (
    <div className={classes.loyaltyPage}>
      <LoyaltyPageHeader
        showLoyaltyConsent={lepTermsAccepted}
        onTermsAccepted={handleTermsAccepted}/>
      {(showCompanyLoyalty && lepTermsAccepted && typeof lepTermsAccepted === "boolean") &&
        <>
          <LoyaltyBadges loyaltyResponse={loyaltyResponse} getLoyaltyList={getLoyaltyList}
                         getRedemptionHistoryCount={getRedemptionHistoryCount}/>
          <RedemptionHistory
            gridOptions={gridOptions}
            setGridOptions={setGridOptions}
            redemptionHistoryResponse={redemptionHistoryResponse}
            redemptionHistoryCountResponse={redemptionHistoryCountResponse}
            getRedemptionHistoryList={getRedemptionHistoryList}
            getRedemptionHistoryCount={getRedemptionHistoryCount}
            getLoyaltyList={getLoyaltyList}
            addRedemption={addRedemption}
            removeFailedRedemptions={removeFailedRedemptions}
            handleDownloadReport={handleDownloadReport}
          />
        </>
      }
    </div>
  )
}

export default Loyalty;
